/**
 * BundleBuilder component
 */
class BundleBuilder extends HTMLElement {
  css = `
  :host {
    display: block;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 1rem;
    margin-bottom: 1rem;
  }

  @media screen and (max-width: 768px) {
    .header {
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }

  .header h2 {
    margin-top: 0;
    margin-bottom: 0.5rem;
  }

  .header-left {
    max-width: 700px;
  }

  .header-right {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
    min-width: 290px;
  }
  `;

  static get observedAttributes() {
    return [
      'type',
      'minimum',
      'minimum_value',
      'product_id',
      'variant_id',
      'sellingplan_id',
      'redirect',
      'subscription',
      'persist_subscription',
    ];
  }

  constructor() {
    super();

    this.total = 0;
    this.totalPrice = 0;
    this.totalSubscriptionPrice = 0;
    this.sellingplan_id = '';
    this.products = [];
    this.productStorage = []; // used to store the progress of the builder in session storage

    const shadowRoot = this.attachShadow({ mode: 'open' });
    const div = document.createElement('div');
    div.innerHTML = `
    <style>${this.css}</style>
    <div class="header">
      <div class="header-left">
        <h2><slot name="heading">Default Heading</slot></h2>
        <slot name="description">No description available</slot>
       <div name="sub-button-wapper" class="heading-button">
        <slot name="subscribe_button"></slot>
       </div>
      </div>
      <div class="header-right">
        <slot name="product-options">No product options available</slot>
        <slot name="button">No button available</slot>
      </div>
    </div>
    <slot name="button-text-wrap">No button available</slot>
    <slot name="progress-bar">No progress bar available</slot>
    <slot name="tabs" class="product-tabs">No product available</slot>
    `;
    shadowRoot.appendChild(div);
  }

  async connectedCallback() {
    this.subscription = this.normalizeSubscriptionValue(this.subscription);
    const toggle = this.querySelector('toggle-button');

    // Read initial state from URL
    const urlSubscription = this.getSubscriptionFromUrl();
    if (urlSubscription !== null) {
      this.subscription = urlSubscription;
    }

    // Update toggle UI
    if (toggle) {
      toggle.setAttribute('selected', this.subscription);
    }

    // Check if persist subscription is enabled
    if (this.persist_subscription == 'true') {
      console.log("Restoring builder's progress from session storage");
      // Check if the session storage exists and update the spinners
      this.productStorage = this.getSessionStorage();
      if (this.productStorage) {
        this.productStorage.forEach((product) => {
          const spinner = this.querySelector(`quantity-spinner[data-variant-id="${product.externalVariantId}"]`);
          spinner.quantity = product.quantity;
        });
      }
    }

    // Render subscription prices
    this.renderSubscriptionPrices(this.subscription);

    // Set initial values
    this.total = this.calculateTotal();
    this.totalPrice = this.calculateTotalPrice();
    this.totalSubscriptionPrice = this.calculateTotalSubscriptionPrice();

    // Update progress bar
    this.updateProgressBar();
    this.updateTotalPrice(this.subscription);
    this.updateTotalSubscriptionPrice(this.subscription);

    // Add event listeners
    this.querySelector('#button--create').addEventListener('click', this.onCreateButtonClick);
    this.querySelectorAll('quantity-spinner').forEach((spinner) => {
      spinner.addEventListener('change', this.onQuantityChange);
    });
    this.querySelector('toggle-button').addEventListener('change', this.onToggleChange);
    const sellingPlanSelector = this.querySelector('selling-plan-selector');
    if (sellingPlanSelector) {
      sellingPlanSelector.style.display = this.subscription === 'true' ? 'block' : 'none';
      sellingPlanSelector.addEventListener('selector-change', this.onSellingPlanChange);
    }
    // this.querySelector('selling-plan-selector').style.display = "none";
  }

  // Function to get subscription from URL parameter
  getSubscriptionFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get('subscription');
    if (param === 'true') return 'true';
    if (param === 'false') return 'false';
    return null; // No parameter set
  }

  normalizeSubscriptionValue(value) {
    return value === true || value === 'true' ? 'true' : 'false';
  }

  setSubscriptionInUrl(isSubscription) {
    const url = new URL(window.location);
    url.searchParams.set('subscription', isSubscription ? 'true' : 'false');
    window.history.replaceState({}, '', url);
  }

  // Function to read bundle product id from the attribute
  get product_id() {
    const value = this.getAttribute('product_id');
    if (isNaN(value)) return 0;
    if (value < 0) return 0;
    return Number(value);
  }

  // Function to read bundle variant id from the attribute
  get variant_id() {
    const value = this.getAttribute('variant_id');
    if (isNaN(value)) return 0;
    if (value < 0) return 0;
    return Number(value);
  }

  // Function to read selling plan id from the attribute
  get sellingplan_id() {
    const value = this.getAttribute('sellingplan_id');
    return value || ''; // Return empty string if null/undefined;
  }

  // Function to read the type of bundle from the attribute
  get type() {
    return this.getAttribute('type');
  }

  // Function to write the type of bundle to the attribute
  set type(value) {
    this.setAttribute('type', value);
  }

  // Function to read the subscription setting of bundle from the attribute
  get subscription() {
    return this.getAttribute('subscription');
  }

  // Function to write the subscription setting of bundle to the attribute
  set subscription(value) {
    this.setAttribute('subscription', value);
  }

  // Function to read the persist_subscription setting of bundle from the attribute
  get persist_subscription() {
    return this.getAttribute('persist_subscription');
  }

  // Function to write the persist_subscription setting of bundle to the attribute
  set persist_subscription(value) {
    this.setAttribute('persist_subscription', value);
  }

  // Function to read minimum value from the attribute
  get minimum() {
    const value = this.getAttribute('minimum');
    if (isNaN(value)) return 0;
    if (value < 0) return 0;
    return Number(value);
  }

  // Function to write minimum value to the attribute
  set minimum(value) {
    this.setAttribute('minimum', value);
  }

  // Function to read minimum value from the attribute
  get minimum_value() {
    const value = this.getAttribute('minimum_value');
    if (isNaN(value)) return 0;
    if (value < 0) return 0;
    return Number(value);
  }

  // Function to write minimum value to the attribute
  set minimum_value(value) {
    this.setAttribute('minimum_value', value);
  }

  set sellingplan_id(value) {
    this.setAttribute('sellingplan_id', value);
  }

  // Function to read the redirect of bundle from the attribute
  get redirect() {
    return this.getAttribute('redirect');
  }

  // Function to write the redirect of bundle to the attribute
  set redirect(value) {
    this.setAttribute('redirect', value);
  }

  // Function to handle create button click event
  onCreateButtonClick = async () => {
    // Update button state
    const button = this.querySelector('#button--create');
    button.disabled = true;
    button.querySelector('svg').classList.remove('tw-hidden');
    button.querySelector('span').classList.add('tw-inline');
    button.querySelector('span').innerHTML = `
      Adding to cart...
    `;

    // Build product array
    this.products = this.generateProductArray(this.subscription);
    const bundle = {
      externalProductId: this.product_id,
      externalVariantId: this.variant_id,
      selections: this.products,
    };

    // Check to see if the bundle is valid
    const isValid = recharge.bundle.validateDynamicBundle(bundle);
    if (isValid === true) {
      // Do on valid
      const bundleItems = await recharge.bundle.getDynamicBundleItems(bundle, 'build-your-own-bundle');

      // Add bundle to cart
      this.addBundleToCart({
        items: bundleItems,
      });
    } else {
      console.log('Bundle is not valid: ', isValid);
    }
  };

  // Function to handle subscription toggle change event
  onToggleChange = (event) => {
    this.subscription = this.normalizeSubscriptionValue(event.detail);
    const sellingPlanSelector = this.querySelector('selling-plan-selector');
    if (sellingPlanSelector) {
      sellingPlanSelector.style.display = this.subscription === 'true' ? 'block' : 'none';
    }

    // Re-render subscription prices when subscription changes
    this.renderSubscriptionPrices(this.subscription);
    this.updateProgressBar();
    this.updateTotalPrice(this.subscription);
    this.updateTotalSubscriptionPrice(this.subscription);

    const params = new URLSearchParams(window.location.search);
    params.set('subscription', this.subscription == 'true' ? 'true' : 'false');

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  // Function to handle selling plan change event
  onSellingPlanChange = (event) => {
    this.sellingplan_id = event.detail;

    // Re-render subscription prices when selling plan changes
    this.renderSubscriptionPrices(this.subscription);
    this.updateProgressBar();
    this.updateTotalPrice(this.subscription);
    this.updateTotalSubscriptionPrice(this.subscription);
  };

  // Function to handle quantity change event
  onQuantityChange = (event) => {
    this.updateProgressBar();
    this.updateTotalPrice(this.subscription);
    this.updateTotalSubscriptionPrice(this.subscription);
  };

  // Function to update the progress bar and button state
  updateProgressBar = () => {
    const button = this.querySelector('#button--create');
    let percentage = 0;
    let total = 0;
    let minimum = 0;
    let displayAmount = '';

    this.total = this.calculateTotal();
    this.totalPrice = this.calculateTotalPrice();
    this.totalSubscriptionPrice = this.calculateTotalSubscriptionPrice();

    switch (this.type) {
      case 'quantity':
        total = this.total;
        minimum = this.minimum;
        displayAmount = this.minimum - this.total;
        break;

      case 'price':
        total = this.totalPrice;
        minimum = this.minimum_value;
        displayAmount = `£${((this.minimum_value - this.totalPrice) / 100).toFixed(2)}`;
        break;
    }

    // Check if the total quantity is less than the minimum
    if (total < minimum) {
      button.disabled = true;
      button.classList.add('tw-opacity-50');
      button.querySelector('span').innerHTML = `Add ${displayAmount} more to cart`;
    } else {
      button.disabled = false;
      button.classList.remove('tw-opacity-50');
      button.querySelector('span').innerHTML = 'Add to cart';
    }

    // Update progress bar
    percentage = (total / minimum) * 100;
    const progressBar = this.querySelector('progress-bar');
    progressBar.percent = percentage;

    // Update session storage
    this.setSessionStorage();
  };

  // Function to update total value in the header
  updateTotalPrice = (subscription) => {
    const totalValueElement = this.querySelector('#bundle-total-onetime-value');
    if (totalValueElement) {
      const formattedPrice = `£${(this.totalPrice / 100).toFixed(2)}`;
      totalValueElement.innerHTML = formattedPrice;
      totalValueElement.classList.toggle('hidden', subscription === 'true');
    } else {
      console.warn('Total value element not found in the header');
    }
  };

  // Function to update total subscription value in the header
  updateTotalSubscriptionPrice = (subscription) => {
    const totalSubscriptionValueElement = this.querySelector('#bundle-total-subscription-value');
    if (totalSubscriptionValueElement) {
      const formattedPrice = `£${(this.totalSubscriptionPrice / 100).toFixed(2)}`;
      totalSubscriptionValueElement.innerHTML = formattedPrice;
      totalSubscriptionValueElement.classList.toggle('hidden', subscription !== 'true');
    } else {
      console.warn('Total subscription value element not found in the header');
    }
  };

  // Function to calculate total quantity
  calculateTotal = () => {
    const spinners = this.querySelectorAll('quantity-spinner');
    let total = 0;
    spinners.forEach((spinner) => {
      total += parseInt(spinner.quantity, 10);
    });
    return total;
  };

  // Function to get the subscription price of each product
  renderSubscriptionPrices = (subscription) => {
    // Get selling plan id from the getter (not attribute directly)
    const sellingPlanId = this.sellingplan_id;

    const variants = this.querySelectorAll('.card__variants-item');
    variants.forEach((variant) => {
      const sellingPlans = variant.querySelectorAll('.card__variant-selling-plans li');
      sellingPlans.forEach((sellingPlan) => {
        if (sellingPlan.dataset.sellingPlanName === this.sellingplan_id) {
          // Get the price element
          const subscriptionPriceElement = variant.querySelector('.card__variants-subscription-price');
          const priceElement = variant.querySelector('.card__variants-price');
          if (subscriptionPriceElement) {
            // Update the price element with the selling plan price
            const formattedPrice = `£${(sellingPlan.dataset.sellingPlanPrice / 100).toFixed(2)}`;
            subscriptionPriceElement.innerHTML = formattedPrice;
            subscriptionPriceElement.dataset.subscriptionPrice = sellingPlan.dataset.sellingPlanPrice;
            if (subscription == 'true') {
              subscriptionPriceElement.classList.remove('hidden');
              priceElement.classList.add('hidden');
            } else {
              subscriptionPriceElement.classList.add('hidden');
              priceElement.classList.remove('hidden');
            }
          }
        }
      });
    });
  };

  // Function to initialise the session storage
  setSessionStorage = () => {
    const spinners = this.querySelectorAll('quantity-spinner');
    let data = [];
    spinners.forEach((spinner) => {
      if (spinner.quantity > 0) {
        data.push({
          externalVariantId: spinner.dataset.variantId,
          quantity: spinner.quantity,
        });
      }
    });
    sessionStorage.setItem('bundle-builder-products', JSON.stringify(data));
  };

  // Function to read the session storage
  getSessionStorage = () => {
    return JSON.parse(sessionStorage.getItem('bundle-builder-products'));
  };

  // Function to calculate total price
  calculateTotalPrice = () => {
    const spinners = this.querySelectorAll('quantity-spinner');
    let totalPrice = 0;
    spinners.forEach((spinner) => {
      totalPrice += spinner.quantity * Number(spinner.dataset.price);
    });
    return totalPrice;
  };

  // Function to calculate total subscription price
  calculateTotalSubscriptionPrice = () => {
    const variants = this.querySelectorAll('.card__variants-item');
    let totalSubscriptionPrice = 0;
    variants.forEach((variant) => {
      const subscriptionPriceElement = variant.querySelector('.card__variants-subscription-price');
      const regularPriceElement = variant.querySelector('.card__variants-price');
      const spinner = variant.querySelector('quantity-spinner');

      if (!spinner) return; // Skip if no spinner element

      let priceToUse = '0';

      // Check if subscription price exists and has a value
      if (subscriptionPriceElement && subscriptionPriceElement.dataset.subscriptionPrice) {
        priceToUse = subscriptionPriceElement.dataset.subscriptionPrice;
      }
      // Fall back to regular price if no subscription price
      else if (regularPriceElement && regularPriceElement.dataset.price) {
        priceToUse = regularPriceElement.dataset.price;
      }
      // Fall back to spinner price as last resort
      else if (spinner.dataset.price) {
        priceToUse = spinner.dataset.price;
      }

      // Add null checks and default values
      const quantity = spinner.quantity || 0;
      const itemTotal = quantity * Number(priceToUse);

      if (!isNaN(itemTotal)) {
        totalSubscriptionPrice += itemTotal;
      }
    });
    return totalSubscriptionPrice;
  };

  // Funtion to generate product array
  generateProductArray = (subscription) => {
    const spinners = this.querySelectorAll('quantity-spinner');
    let productArray = [];
    let planId = '';

    // Build product array with or without selling plan
    if (subscription == 'true') {
      spinners.forEach((spinner) => {
        if (spinner.quantity > 0) {
          const spinnerSellingPlans = spinner.querySelectorAll('.card__variant-selling-plans li');
          spinnerSellingPlans.forEach((sellingPlan) => {
            if (sellingPlan.dataset.sellingPlanName === this.sellingplan_id) {
              planId = sellingPlan.dataset.sellingPlanId;
            }
          });
          productArray.push({
            collectionId: spinner.dataset.collectionId,
            externalProductId: spinner.dataset.productId,
            externalVariantId: spinner.dataset.variantId,
            quantity: spinner.quantity,
            sellingPlan: planId,
          });
        }
      });
    } else {
      spinners.forEach((spinner) => {
        if (spinner.quantity > 0) {
          productArray.push({
            collectionId: spinner.dataset.collectionId,
            externalProductId: spinner.dataset.productId,
            externalVariantId: spinner.dataset.variantId,
            quantity: spinner.quantity,
          });
        }
      });
    }
    return productArray;
  };

  // Function to clear the cart
  clearCart = () => {
    return fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to clear the cart');
      }
      return response.json();
    });
  };

  // Function to handle add to cart
  addBundleToCart = async (data) => {
    const button = this.querySelector('#button--create');
    this.clearCart()
      .then(() => {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to add bundle to cart');
        }
        return response.json();
      })
      .then((cart) => {
        console.log('Bundle added to cart: ', cart);
        if (this.redirect == 'checkout') {
          // Redirect to the cart page after successful addition
          window.location.href = '/checkout';
        } else {
          // Redirect to the checkout page after successful addition
          window.location.href = '/cart';
        }
      })
      .catch((error) => {
        console.error('Error: ', error);
      })
      .finally(() => {
        if (!button) return;

        button.querySelector('svg')?.classList.add('tw-hidden');
        button.querySelector('span')?.classList.remove('tw-inline');
        button.querySelector('span').innerHTML = 'Add to cart';
        button.disabled = false;
      });
  };
}

class QuantitySpinner extends HTMLElement {
  css = `
  :host {
    display: block;
    --tw-ring-offset-shadow: 0 0 #0000;
    --tw-ring-shadow: 0 0 #0000;
    --tw-ring-opacity: 1;
    --tw-ring-color: rgb(209 213 219 / var(--tw-ring-opacity));
    --tw-ring-inset: inset;
    --tw-bg-opacity: 1;
    --tw-text-opacity: 1;
    --tw-ring-offset-width: 0px;
    --tw-ring-offset-color: #fff;
  }

  :host([disabled]) {
    pointer-events: none;
  }

  :host([disabled]) .button {
    opacity: 0.5;
  }

  .wrapper {
    display: inline-flex;
    border-radius: 0.375rem;
    --tw-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
  }

  button {
    overflow: visible;
    -webkit-appearance: none;
    background: none;
    border: none;
    cursor: pointer;
    -webkit-transition: all 0.3s linear !important;
    -moz-transition: all 0.3s linear !important;
    -o-transition: all 0.3s linear !important;
    -ms-transition: all 0.3s linear !important;
    transition: all 0.3s linear !important;
    position: relative;
    display: inline-flex;
    align-items: center;
    background-color: rgb(255 255 255 / var(--tw-bg-opacity));
    padding: 0.5rem;
    color: rgb(156 163 175 / var(--tw-text-opacity));
    --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
    --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
    box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000)
  }

  button:hover {
    --tw-bg-opacity: 1;
    background-color: rgb(249 250 251 / var(--tw-bg-opacity));
  }

  button.button--decrement {
    border-top-left-radius: 0.375rem;
    border-bottom-left-radius: 0.375rem;
  }

  button.button--increment {
    border-top-right-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
  }

  button>svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  .quantity {
    min-width: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
    --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
    box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
    margin-left: -1px;
    margin-right: -1px;
    background-color: rgb(255 255 255 / var(--tw-bg-opacity));
  }
  `;

  template = () => `
  <div class="wrapper">
    <button type="button" class="button button--decrement">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5">
        <path fill-rule="evenodd" d="M4 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 10Z" clip-rule="evenodd" />
      </svg>
    </button>
    <div class="quantity">${this.quantity}</div>
    <button type="button" class="button button--increment">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5">
        <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
      </svg>
    </button>
  </div>
  `;

  static get observedAttributes() {
    return ['quantity'];
  }

  constructor() {
    super();

    this._quantity = this.getAttribute('quantity') || 0;
    this.attachShadow({ mode: 'open' });
    this.render();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>${this.css}</style>
      <slot name="sellingplans"></slot>
      ${this.template().trim()}
    `;

    this.shadowRoot.querySelector('.button--increment').addEventListener('click', this.onIncrementButtonClick);
    this.shadowRoot.querySelector('.button--decrement').addEventListener('click', this.onDecrementButtonClick);
  }

  // Function to read quantity value from the attribute
  get quantity() {
    return this._quantity;
  }

  // Function to write quantity value to the attribute
  set quantity(value) {
    this._quantity = value;
    this.setAttribute('quantity', value);
  }

  onIncrementButtonClick = () => {
    this.quantity = parseInt(this.quantity) + 1;
    this.render();
    this.handleChange();
  };

  onDecrementButtonClick = () => {
    this.quantity = Math.max(0, parseInt(this.quantity) - 1);
    this.render();
    this.handleChange();
  };

  handleChange() {
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: this.quantity,
        bubble: true,
        composed: true,
      })
    );
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'quantity') {
      this._quantity = newValue;
      this.render();
    }
  }

  connectedCallback() {
    this.render();
  }
}

class ProgressBar extends HTMLElement {
  static css = `
    :host {
      display: block;
      width: 100%;
      height: 0.5rem;
      background-color: rgb(var(--background-color));
      border-radius: 9999px;
      overflow: hidden;
      margin-bottom: 1rem;
    }

    .fill {
      width: 0%;
      height: 100%;
      border-radius: 9999px;
      background: rgb(var(--fill-color));
      transition: width 0.25s;
    }
  `;

  static get observedAttributes() {
    return ['percent'];
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    const fill = document.createElement('div');

    style.innerHTML = ProgressBar.css;
    fill.classList.add('fill');

    this.shadowRoot.append(style, fill);
  }

  get percent() {
    const value = this.getAttribute('percent');

    if (isNaN(value)) return 0;
    if (value < 0) return 0;
    if (value > 100) return 100;

    return Number(value);
  }

  set percent(value) {
    this.setAttribute('percent', value);
  }

  attributeChangedCallback(name) {
    if (name === 'percent') {
      this.shadowRoot.querySelector('.fill').style.width = `${this.percent}%`;
    }
  }
}

class ToggleButton extends HTMLElement {
  css = `
    button {
      position: relative;
      display: inline-flex;
      height: 1.5rem;
      width: 2.75rem;
      padding: 0;
      cursor: pointer;
      border-radius: 9999px;
      border-width: 2px;
      border-color: transparent;
      background-color: rgb(var(--background-color));
      transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
      transition-duration: 200ms;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    }

    button[aria-checked='true'] {
      background-color: rgb(var(--fill-color));
    }

    button[aria-checked='true'] .inner {
      transform: translate(1.25rem, 0);
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    }

    .inner {
      pointer-events: none;
      display: inline-block;
      height: 1.25rem;
      width: 1.25rem;
      transform: translate(0, 0);
      border-radius: 9999px;
      background-color: #FFFFFF;
      --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 0 #FFFFFF;
      --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + 0) rgb(59 130 246 / 0.5);
      box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1), 0 0 #0000);
      --tw-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
      transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter, -webkit-backdrop-filter;
      transition-duration: 200ms;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    }
  `;

  template = () => `
    <button type="button" class="button" aria-checked="${this.selected}">
      <span class="sr-only">Toggle</span>
      <span class="inner"></span>
    </button>
  `;

  static get observedAttributes() {
    return ['selected'];
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' });
    this.upgradeProperty('selected');
    this.render();
  }

  upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>${this.css}</style>
      ${this.template().trim()}
    `;

    this.shadowRoot.querySelector('button').addEventListener('click', this.onToggleClick);
    this.syncButtonState();
  }

  // Function to read the type of bundle from the attribute
  get selected() {
    return this.getAttribute('selected') === 'true' ? 'true' : 'false';
  }

  // Function to write the type of bundle to the attribute
  set selected(value) {
    this.setAttribute('selected', value === true || value === 'true' ? 'true' : 'false');
  }

  attributeChangedCallback(name) {
    if (name === 'selected') {
      this.syncButtonState();
    }
  }

  syncButtonState = () => {
    const button = this.shadowRoot?.querySelector('button');
    if (!button) return;
    button.ariaChecked = this.selected === 'true' ? 'true' : 'false';
  };

  onToggleClick = () => {
    if (this.disabled) return;

    this.selected = this.selected === 'true' ? 'false' : 'true';
    this.syncButtonState();
    this.handleChange();
  };

  handleChange() {

    this.dispatchEvent(
      new CustomEvent('change', {
        detail: this.selected,
        bubble: true,
        composed: true,
      })
    );
  }

}

class SellingPlanSelector extends HTMLElement {
  constructor() {
    super();

    this.subscriptionid = '';
    this.render();
  }

  render() {
    this.querySelector('select').addEventListener('change', this.onSelectClick);
    this.subscriptionid = this.querySelector('select').value;
    this.handleChange();
  }

  onSelectClick = (event) => {
    this.subscriptionid = event.target.value;
    this.handleChange();
  };

  handleChange() {
    this.dispatchEvent(
      new CustomEvent('selector-change', {
        detail: this.subscriptionid,
        bubble: true,
        composed: true,
      })
    );
  }
}

customElements.define('quantity-spinner', QuantitySpinner);
customElements.define('progress-bar', ProgressBar);
customElements.define('bundle-builder', BundleBuilder);
customElements.define('toggle-button', ToggleButton);
customElements.define('selling-plan-selector', SellingPlanSelector);
